import * as React from 'react';

import { Input } from '@/ux-common/components/input/input';
import { useInputGroup } from '@/ux-common/components/input/input.context';
import {
  InputTriggerField,
  type InputTriggerFieldProps,
} from '@/ux-common/components/input/input-trigger-field';
import {
  MobileMenu,
  MobileMenuContent,
  type MobileMenuContentProps,
  type MobileMenuProps,
  MobileMenuTrigger,
} from '@/ux-common/components/mobile-menu/mobile-menu';
import { cn } from '@/ux-common/lib/utils';

/**
 * MobileMenuInput — the phone twin of DropdownInput: a form field that opens a
 * MobileMenu bottom sheet. The Input's bordered field is drawn as a button, showing
 * a value you write (or a placeholder) and a chevron.
 *
 * It is an `Input` group, like DropdownInput and Select: `InputLabel` and
 * `InputHelper` wire themselves to the trigger with no ids at the call site, and
 * every root state (`state="new"`, `.input-lg`, …) applies. The trigger is named
 * after the label and the value.
 *
 * The sheet is `MobileMenuInputContent`, named after the label. Its rows are the
 * MobileMenu parts: `MobileMenuRadioGroup` / `MobileMenuRadioItem` for one value (a
 * pick closes the sheet), `MobileMenuCheckboxItem` for several (they keep it open).
 * The app owns the value and writes the field's text.
 *
 * @example
 * ```tsx
 * <MobileMenuInput state={changed ? 'new' : undefined} disabledReason={reason}>
 *   <InputLabelRow>
 *     <InputLabel>Mode</InputLabel>
 *     <InputLabelInfo info="How the strategy picks its setpoint." />
 *   </InputLabelRow>
 *   <MobileMenuInputTrigger>
 *     {currentLabel}
 *     {changed && <InputValueChange oldValue={initialLabel} />}
 *   </MobileMenuInputTrigger>
 *   <MobileMenuInputContent>
 *     <MobileMenuRadioGroup value={value} onValueChange={onChange}>
 *       {MODES.map((mode) => (
 *         <MobileMenuRadioItem key={mode.value} value={mode.value}>
 *           {mode.label}
 *         </MobileMenuRadioItem>
 *       ))}
 *     </MobileMenuRadioGroup>
 *   </MobileMenuInputContent>
 * </MobileMenuInput>
 * ```
 */
export type MobileMenuInputProps = MobileMenuProps & {
  /** Class(es) for the `.input` group that wraps the field. */
  className?: string;
  /**
   * The group's validation state, as on `Input` — applies `.input-{state}` and
   * reaches the group's slots.
   */
  state?: React.ComponentProps<typeof Input>['state'];
  /**
   * Refuses the field and says why — a view-only session, a missing permission.
   * The trigger stays focusable and hoverable, so a keyboard user reaches it and a
   * `withTooltip` on it still fires. It reads and looks disabled (`aria-disabled`),
   * never opens the sheet, and announces the reason as its description without
   * depending on hover. Wins over the trigger's `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
  /**
   * The group's `data-component` marker — `"MobileMenuInput"` unless you name it.
   * The MobileMenu root renders no element, so this is the field's outermost
   * element: a call site annotating its own component root sets it here.
   */
  'data-component'?: string;
};

/**
 * The root's refusal, for the trigger: the id of the visually hidden reason while
 * `disabledReason` is set.
 */
const MobileMenuInputRefusalContext = React.createContext<string | undefined>(undefined);

/**
 * MobileMenuInput — the MobileMenu root inside an `.input.mobile-menu-input` group.
 * Root props (`open`, `defaultOpen`, `onOpenChange`, `modal`) pass through;
 * `className` and `state` go to the group.
 *
 * A refused field (`disabledReason`) is held shut here, whatever opens it: the
 * sheet is controlled closed and ignores open requests. The reason is rendered
 * once, visually hidden, for the trigger's `aria-describedby`.
 */
export const MobileMenuInput = ({
  className,
  state,
  children,
  disabledReason,
  'data-component': dataComponent = 'MobileMenuInput',
  ...props
}: MobileMenuInputProps) => {
  const reasonId = React.useId();
  const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';
  const rootProps = refused ? { ...props, open: false, onOpenChange: undefined } : props;

  return (
    <MobileMenu {...rootProps}>
      <MobileMenuInputRefusalContext.Provider value={refused ? reasonId : undefined}>
        <Input
          data-component={dataComponent}
          state={state}
          className={cn('mobile-menu-input', className)}
        >
          {children}
          {refused && (
            <span id={reasonId} className="sr-only">
              {disabledReason}
            </span>
          )}
        </Input>
      </MobileMenuInputRefusalContext.Provider>
    </MobileMenu>
  );
};

MobileMenuInput.displayName = 'MobileMenuInput';

export type MobileMenuInputTriggerProps = Omit<
  InputTriggerFieldProps,
  'refusalId' | 'valueClassName' | 'chevronClassName'
>;

/**
 * MobileMenuInputTrigger — the field. The shared `InputTriggerField` (the same
 * button DropdownInputTrigger draws) under `MobileMenuTrigger asChild`, so it is a
 * `<button aria-haspopup="dialog">` that opens the sheet, and its chevron turns
 * while the sheet is open.
 *
 * It joins the enclosing group, so `InputLabel`'s `htmlFor` and every
 * `InputHelper`'s description reach it. Its name is the label AND the value ("Mode
 * Auto"). Pass `InputLeadingIcon`, `InputValueChange` (`state="new"`) and
 * `InputStateIcon` as children, as on DropdownInputTrigger.
 *
 * The label points at this button, so a tap on the label opens the sheet, as a tap
 * on the field does.
 */
export const MobileMenuInputTrigger = React.forwardRef<
  HTMLButtonElement,
  MobileMenuInputTriggerProps
>(({ className, id, disabled, ...props }, ref) => {
  const reasonId = React.useContext(MobileMenuInputRefusalContext);
  const refused = reasonId !== undefined;

  return (
    <MobileMenuTrigger asChild disabled={refused ? false : disabled}>
      <InputTriggerField
        ref={ref}
        // Passed even when undefined, so the trigger's props never replace the
        // group's control id (the Slot lets the child's props win).
        id={id}
        disabled={disabled}
        refusalId={reasonId}
        data-component="MobileMenuInputTrigger"
        className={cn('mobile-menu-input-trigger', className)}
        valueClassName="mobile-menu-input-value"
        chevronClassName="mobile-menu-input-chevron"
        {...props}
      />
    </MobileMenuTrigger>
  );
});

MobileMenuInputTrigger.displayName = 'MobileMenuInputTrigger';

export type MobileMenuInputContentProps = MobileMenuContentProps;

/**
 * MobileMenuInputContent — the sheet. A `MobileMenuContent` named after the
 * group's `InputLabel`, so the label is written once. Its `title` (the sheet's
 * visually hidden name) defaults to the label's text; pass `title` to name it
 * otherwise. With no label in the group it falls back to MobileMenuContent's
 * `"Menu"`.
 *
 * Fill it with MobileMenu rows: `MobileMenuRadioGroup` / `MobileMenuRadioItem`
 * for one value, `MobileMenuCheckboxItem` for several.
 */
export const MobileMenuInputContent = React.forwardRef<HTMLDivElement, MobileMenuInputContentProps>(
  ({ title, className, ...props }, ref) => {
    const labelId = useInputGroup()?.labelId;

    return (
      <MobileMenuContent
        ref={ref}
        title={title ?? <LabelText labelId={labelId} />}
        data-component="MobileMenuInputContent"
        className={cn('mobile-menu-input-content', className)}
        {...props}
      />
    );
  }
);

MobileMenuInputContent.displayName = 'MobileMenuInputContent';

/**
 * The group label's text, for the sheet's title. The label renders in the field,
 * not in the sheet, so its text is read from the DOM. This mounts with the sheet,
 * so it reads the label as it is on each open; a sheet open on the first render
 * reads it once the label has registered.
 */
const LabelText = ({ labelId }: { labelId: string | undefined }) => {
  const [text, setText] = React.useState<string>();
  React.useLayoutEffect(() => {
    setText(labelId ? document.getElementById(labelId)?.textContent?.trim() : undefined);
  }, [labelId]);
  return <>{text || 'Menu'}</>;
};
